import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import BoxCard from "@/components/BoxCard";
import SEOHead, { collectionPageSchema, breadcrumbSchema } from "@/components/SEOHead";
import PageHero from "@/components/PageHero";
import PostcodeChecker from "@/components/PostcodeChecker";
import { Award, Truck, Repeat, Clock, Users } from "lucide-react";

const HERO_CHIPS = [
  { icon: Award, label: "Halal Certified" },
  { icon: Truck, label: "Free Perth Delivery" },
  { icon: Repeat, label: "Subscribe & Save 5%" },
  { icon: Clock, label: "Order Thu · Deliver Sat" },
];

/** Household-size guidance — the box grid can be overwhelming when
 * shoppers land cold. This selector highlights the boxes that match
 * their household size. */
const HOUSEHOLD_FILTERS = {
  "1-2": { label: "1–2", slugs: ["starter-box", "performance-box"] },
  "3-4": { label: "3–4", slugs: ["family-feast-box", "gluten-free-box"] },
  "5+":  { label: "5+",  slugs: ["family-feast-box", "grill-masters-box"] },
};

/** Sort so Family Feast is always the first card in the grid. */
function sortWithFamilyFeastFirst(list) {
  const sorted = [...list];
  sorted.sort((a, b) => {
    if (a.slug === "family-feast-box") return -1;
    if (b.slug === "family-feast-box") return 1;
    return 0;
  });
  return sorted;
}

export default function Boxes() {
  const [boxes, setBoxes] = useState([]);
  const [household, setHousehold] = useState(null); // null | "1-2" | "3-4" | "5+"
  useEffect(() => { api.get("/boxes").then((r) => setBoxes(r.data)).catch(() => {}); }, []);

  const displayBoxes = useMemo(() => {
    const sorted = sortWithFamilyFeastFirst(boxes);
    if (!household) return sorted;
    const highlights = new Set(HOUSEHOLD_FILTERS[household].slugs);
    // Move highlighted boxes to the top; dim the rest visually.
    const inSet = sorted.filter((b) => highlights.has(b.slug));
    const rest  = sorted.filter((b) => !highlights.has(b.slug));
    return [...inSet, ...rest];
  }, [boxes, household]);

  return (
    <div className="pb-24" data-testid="boxes-page">
      <SEOHead
        title="Curated Meat Boxes — 7 hand-picked Perth boxes"
        description="Seven curated meat boxes from $99 — Starter, Gluten Free, Performance, Braai, Family Feast, Carnivore Collection & Grill Master's. 100% Halal, Australian-sourced, packed at our Malaga butchery. Subscribe & save 5% + free Wagyu burgers with every Perth metro delivery."
        path="/boxes"
        ogImage="/boxes/family-feast-box.jpg"
        jsonLd={boxes.length > 0 ? [
          collectionPageSchema({
            name: "Curated Meat Boxes",
            description: "Seven curated Perth-delivered meat boxes — halal certified, restaurant-quality, portioned for real households.",
            path: "/boxes",
            heroImage: "/boxes/family-feast-box.jpg",
            products: displayBoxes.map((b) => ({
              name: b.name,
              slug: b.slug,
              price: b.subscription_price ?? b.price,
              image: b.image,
              description: b.description,
            })),
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Curated Boxes", path: "/boxes" },
          ]),
        ] : []}
      />

      {/* Wholesale-style hero — full-bleed dark backdrop, single image */}
      <PageHero
        testId="boxes-hero"
        image="/media/craft-poster.jpg"
        imageAlt="A Perth family unboxing their weekly Butcher Box delivery in a bright kitchen"
        eyebrow="Curated Meat Boxes · Est. Perth WA"
        title={<>Seven boxes.<br /><span className="text-[color:var(--tbb-burgundy)]">One weekend of great meat.</span></>}
        subhead="Hand-picked by our butchers, packed at 15 Action Road, Malaga, and delivered cold across Perth every Saturday. Each box is 100% Halal certified, Australian-sourced, and portioned for real households."
      >
        <div className="flex flex-wrap gap-2 mb-3" data-testid="boxes-hero-chips">
          {HERO_CHIPS.map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 border border-white/25 text-white/85 backdrop-blur-sm"
            >
              <Icon size={12} className="text-[color:var(--tbb-burgundy)]" />
              {label}
            </span>
          ))}
        </div>
        <div className="text-[11px] uppercase tracking-widest text-white/70">
          From <span className="text-white font-bold">$99</span> · Save 5% + a free pack of Wagyu Beef Burgers each delivery with any subscription
        </div>
      </PageHero>

      {/* Delivery band — postcode check stays above-the-fold on desktop */}
      <section className="bg-[color:var(--tbb-cream)] border-b border-[color:var(--tbb-line)]" data-testid="boxes-delivery-band">
        <div className="tbb-container py-6 md:py-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 md:gap-8 items-center">
          <div className="max-w-md">
            <div className="text-[10px] font-black uppercase tracking-[0.28em] text-[color:var(--tbb-burgundy)] mb-1">
              Delivery Check · Perth Metro
            </div>
            <div className="text-sm text-[color:var(--tbb-ink)]/80">
              Order Thursday 11pm · packed Friday · delivered Saturday. Confirm your postcode first — free delivery on every order.
            </div>
          </div>
          <div className="w-full md:min-w-[380px] md:max-w-md">
            <PostcodeChecker variant="hero" />
          </div>
        </div>
      </section>

      {/* Boxes grid */}
      <div className="tbb-container mt-12 md:mt-16">
        {/* Household-size guidance */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="household-selector">
          <div className="inline-flex items-center gap-2">
            <Users size={16} className="text-[color:var(--tbb-burgundy)]" />
            <span className="text-sm font-bold uppercase tracking-widest">Feeding how many?</span>
          </div>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Filter boxes by household size">
            {Object.entries(HOUSEHOLD_FILTERS).map(([key, cfg]) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={household === key}
                onClick={() => setHousehold(household === key ? null : key)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition ${
                  household === key
                    ? "bg-[color:var(--tbb-burgundy)] text-white border-[color:var(--tbb-burgundy)]"
                    : "border-[color:var(--tbb-line)] hover:border-black"
                }`}
                data-testid={`household-${key}`}
              >
                {cfg.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setHousehold(null)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest ${
                household ? "text-[color:var(--tbb-burgundy)] underline" : "text-[color:var(--tbb-muted)]"
              }`}
              data-testid="household-reset"
            >
              Show all
            </button>
          </div>
        </div>

        {/* Grid — content-start prevents CSS Grid stretch. min-h reservation
            removed Sep 2026 for the same reason as BuildYourOwn — a partly-
            loaded grid was stretching each row to fill reserved height,
            leaving giant blank spaces INSIDE each BoxCard in FB/IG in-app
            browsers. Cold loads accept a small footer-jump; the App.js
            API prefetch keeps hot loads shift-free. */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 content-start"
          data-testid="boxes-grid"
        >
          {displayBoxes.map((b, i) => {
            const isRecommended = !household || HOUSEHOLD_FILTERS[household].slugs.includes(b.slug);
            return (
              <div
                key={b.slug}
                className={household && !isRecommended ? "opacity-40 hover:opacity-100 transition" : ""}
                data-testid={`box-slot-${b.slug}`}
              >
                <BoxCard box={b} index={i} priority={i < 4} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
